/**
 * decide.ts — Public entry point for the logic-engine.
 *
 * decide(rules, ctx, screenId): DecisionResult
 *
 * Nav pass (trigger_scope='screen'):
 *   Filters rules where scope_ref === screenId.
 *   First-match-wins: returns {action} of the first rule that evaluates TRUE.
 *   Returns null if no rules match (caller F1b applies fallback-tier logic).
 *
 * Visibility pass (trigger_scope='element'):
 *   Evaluates all element-scope rules.
 *   First-match-wins per blockId: once a TRUE match is recorded for a blockId,
 *   subsequent rules for the same blockId are ignored.
 *   Returns Map<blockId, 'SHOW'|'HIDE'> for rules that evaluate TRUE.
 *
 *   ⚠ NON-RENDER-AUTHORITATIVE: this map records only rules that fired TRUE; it
 *   CANNOT express conditional-show (SHOW rule whose condition is FALSE → the
 *   block must hide). The player's render path therefore uses
 *   `player/visibility.ts resolveVisibility`, which has the full semantics.
 *   Only the nav engine (F1b) consumes decide() today (via `.nav`). Do NOT wire
 *   `.visibility` into a renderer — use resolveVisibility (WEB-170 F-2).
 *
 * Fail-open: broken rules are skipped (evaluate → false), not thrown.
 *
 * Pure module: no React/DOM/navigator/payment-SDK imports.
 * F1b imports `.nav` from here; F1c visibility lives in player/visibility.ts.
 */

import type { Logic } from './rule.schema';
import type { EvalContext, DecisionResult } from './types';
import { evaluate } from './evaluate';

/**
 * decide — evaluate all rules for the current screen.
 *
 * @param rules    - Full Logic array (Rule[]) from the funnel/quiz data
 * @param ctx      - EvalContext snapshot (populated by F1d)
 * @param screenId - Current screen id (used to filter nav rules by scope_ref)
 * @returns DecisionResult with nav action (or null) and visibility map
 */
export function decide(
  rules: Logic,
  ctx: EvalContext,
  screenId: string
): DecisionResult {
  let nav: DecisionResult['nav'] = null;
  const visibility = new Map<string, 'SHOW' | 'HIDE'>();

  for (const rule of rules) {
    if (rule.trigger_scope === 'screen') {
      // Nav pass: only rules for the current screen
      if (rule.scope_ref !== screenId) continue;
      // first-match-wins: stop on first TRUE nav rule
      if (nav !== null) continue;
      try {
        if (evaluate(rule, ctx)) {
          // WEB-2164: id правила едет вместе с действием — по нему серверная
          // точка ухода достаёт адрес назначения из опубликованного
          // содержимого (адреса в запросе нет, см. types.ts).
          nav = { action: rule.action, ruleId: rule.id };
        }
      } catch {
        // fail-open: skip broken rule, continue
      }
    } else if (rule.trigger_scope === 'element') {
      // Visibility pass: element-scope rules (scope_ref = blockId)
      // first-match-wins per blockId: skip if already resolved
      if (visibility.has(rule.scope_ref)) continue;
      try {
        if (evaluate(rule, ctx)) {
          const actionType = rule.action.type;
          if (actionType === 'SHOW' || actionType === 'HIDE') {
            visibility.set(rule.scope_ref, actionType);
          }
        }
      } catch {
        // fail-open: skip broken rule
      }
    }
    // Unknown trigger_scope → skip (fail-open)
  }

  return { nav, visibility };
}
